/**
 * Header search suggestions. As you type, fetches up to 6 products from
 * Shopify's predictive search, rendered by sections/fs-predictive-search.liquid
 * (photo, name, yard price). Follows the ARIA combobox pattern: arrow keys
 * move through results, Enter opens one, Escape closes the list.
 */

const SECTION_ID = 'fs-predictive-search';
const MIN_CHARS = 2;
const DELAY = 180;

class FsSearch {
  /** @param {HTMLFormElement} form */
  constructor(form) {
    this.form = form;
    this.input = /** @type {HTMLInputElement} */ (form.querySelector('[data-fs-search-input]'));
    this.panel = /** @type {HTMLElement} */ (form.querySelector('[data-fs-search-panel]'));
    this.status = form.querySelector('[data-fs-search-status]');
    this.url = form.dataset.suggestUrl || '/search/suggest';
    this.listId = this.input.getAttribute('aria-controls') || '';
    /** @type {Map<string, string>} */
    this.cache = new Map();
    /** @type {AbortController | null} */
    this.controller = null;
    this.timer = 0;
    this.active = -1;

    // The panel itself carries the id until a listbox arrives
    this.panel.id = `${this.listId}-panel`;

    this.input.addEventListener('input', () => this.queue());
    this.input.addEventListener('focus', () => {
      if (this.panel.innerHTML && this.input.value.trim().length >= MIN_CHARS) this.open();
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    form.addEventListener('focusout', (event) => {
      const next = /** @type {Node | null} */ (event.relatedTarget);
      if (!next || !form.contains(next)) this.close();
    });
    document.addEventListener('pointerdown', (event) => {
      if (!form.contains(/** @type {Node} */ (event.target))) this.close();
    });
    this.panel.addEventListener('pointermove', (event) => {
      const option = /** @type {HTMLElement} */ (event.target).closest('[role="option"]');
      if (option) this.setActive(this.options.indexOf(/** @type {HTMLElement} */ (option)), false);
    });
  }

  /** @returns {HTMLElement[]} */
  get options() {
    return Array.from(this.panel.querySelectorAll('[role="option"]'));
  }

  queue() {
    window.clearTimeout(this.timer);
    const terms = this.input.value.trim();
    if (terms.length < MIN_CHARS) {
      this.controller?.abort();
      this.close();
      this.panel.innerHTML = '';
      return;
    }
    this.timer = window.setTimeout(() => this.fetch(terms), DELAY);
  }

  /** @param {string} terms */
  async fetch(terms) {
    const key = terms.toLowerCase();
    let html = this.cache.get(key);

    if (html === undefined) {
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: terms,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: SECTION_ID,
      });
      try {
        const response = await fetch(`${this.url}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        html = doc.querySelector('.fs-ps')?.outerHTML || '';
        this.cache.set(key, html);
      } catch (error) {
        if (/** @type {Error} */ (error).name === 'AbortError') return;
        // Leave the plain search form working; Enter still goes to the results page
        this.close();
        return;
      }
    }

    // The box may have changed while we waited
    if (this.input.value.trim().toLowerCase() !== key) return;
    this.render(html);
  }

  /** @param {string} html */
  render(html) {
    this.panel.innerHTML = html;
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');

    const list = this.panel.querySelector('[role="listbox"]');
    if (list) {
      list.id = this.listId;
    }

    const count = this.options.length;
    if (this.status) {
      this.status.textContent = !html
        ? ''
        : count === 0
          ? 'No fabrics found.'
          : `${count} ${count === 1 ? 'fabric' : 'fabrics'} found. Use the arrow keys to browse.`;
    }

    if (html && document.activeElement === this.input) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', this.options.length > 0 ? 'true' : 'false');
  }

  close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
  }

  /**
   * @param {number} index
   * @param {boolean} [scroll]
   */
  setActive(index, scroll = true) {
    const options = this.options;
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    this.active = index;
    const option = options[index];
    if (option) {
      this.input.setAttribute('aria-activedescendant', option.id);
      if (scroll) option.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    const options = this.options;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!options.length) return;
        event.preventDefault();
        if (this.panel.hidden) this.open();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        let next = this.active + step;
        if (next >= options.length) next = 0;
        if (next < 0) next = options.length - 1;
        this.setActive(next);
        break;
      }
      case 'Enter': {
        const option = !this.panel.hidden ? options[this.active] : null;
        if (option instanceof HTMLAnchorElement) {
          event.preventDefault();
          window.location.href = option.href;
        }
        break;
      }
      case 'Escape': {
        if (!this.panel.hidden) {
          event.preventDefault();
          this.close();
        } else if (this.input.value) {
          event.preventDefault();
          this.input.value = '';
          this.panel.innerHTML = '';
        }
        break;
      }
    }
  }
}

document.querySelectorAll('form[data-fs-search]').forEach((form) => {
  new FsSearch(/** @type {HTMLFormElement} */ (form));
});
